import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Box,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useReferenceLists } from "@/context/GlobalGetLists";
import type { UserAllocation } from "@/types/userAllocations";
import {
  activeStatusIdsExcludingClosed,
  getUserAllocations,
} from "@/pages/userAllocations/userAllocationsApi";
import { getInitials } from "@/utils/noteUtils";
import "@/assets/css/UserAllocationsTally.css";

const avatarColors = ["#1B7A63", "#0E4664", "#5B6C8F", "#00745e", "#182737"];

function isMovingToTarget(
  event: React.MouseEvent,
  target: HTMLElement | null,
): boolean {
  const related = event.relatedTarget;
  return related instanceof Node && Boolean(target?.contains(related));
}

export function UserAllocationsTally() {
  const { status: allStatuses, loading: listsLoading } = useReferenceLists();
  const [hoverOpen, setHoverOpen] = useState(false);
  const [rows, setRows] = useState<UserAllocation[]>([]);
  const [loading, setLoading] = useState(true);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const statusIds = useMemo(
    () => activeStatusIdsExcludingClosed(allStatuses),
    [allStatuses],
  );

  useEffect(() => {
    if (listsLoading) return;

    let cancelled = false;
    setLoading(true);

    getUserAllocations({
      dealsActive: "active",
      statusIds,
    })
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          setRows(response.data.allocations);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [listsLoading, statusIds]);

  const peopleCount = rows.length;

  const handlePopoverLeave = (event: React.MouseEvent) => {
    if (isMovingToTarget(event, triggerRef.current)) return;
    setHoverOpen(false);
  };

  const handleTriggerLeave = (event: React.MouseEvent) => {
    if (isMovingToTarget(event, popoverRef.current)) return;
    setHoverOpen(false);
  };

  return (
    <Box className="user-allocations-tally">
      {hoverOpen && (
        <Paper
          ref={popoverRef}
          elevation={0}
          className="user-allocations-tally__popover"
          onMouseEnter={() => setHoverOpen(true)}
          onMouseLeave={handlePopoverLeave}
        >
          <Box className="user-allocations-tally__popover-header">
            <Box>
              <Typography className="user-allocations-tally__title">
                Deal Lead &amp; Support Tally
              </Typography>
              <Typography className="user-allocations-tally__subtitle">
                Active deals only. Does not include closed deals.
              </Typography>
            </Box>
            <Typography className="user-allocations-tally__people-count">
              {peopleCount} {peopleCount === 1 ? "Person" : "People"}
            </Typography>
          </Box>

          {loading ? (
            <Box className="user-allocations-tally__loading">
              <CircularProgress size={22} sx={{ color: "#1B7A63" }} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography className="user-allocations-tally__empty">
              No active allocations to show.
            </Typography>
          ) : (
            <Table size="small" className="user-allocations-tally__table">
              <TableHead>
                <TableRow>
                  <TableCell>Person</TableCell>
                  <TableCell align="right">
                    <span className="user-allocations-tally__dot user-allocations-tally__dot--lead" />
                    Deal Lead
                  </TableCell>
                  <TableCell align="right">
                    <span className="user-allocations-tally__dot user-allocations-tally__dot--support" />
                    Deal Support
                  </TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.user_id}>
                    <TableCell>
                      <Box className="user-allocations-tally__person">
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            bgcolor: avatarColors[index % avatarColors.length],
                          }}
                        >
                          {getInitials(row.name)}
                        </Avatar>
                        <Typography
                          component="span"
                          className="user-allocations-tally__person-name"
                          title={row.name}
                        >
                          {row.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{row.deal_lead.deal_count}</TableCell>
                    <TableCell align="right">{row.deal_support.deal_count}</TableCell>
                    <TableCell align="right" className="user-allocations-tally__total">
                      {row.combined.deal_count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Link
            component={RouterLink}
            to="/summary?tab=user-allocations"
            target="_blank"
            rel="noopener noreferrer"
            className="user-allocations-tally__link"
          >
            View full breakdown →
          </Link>
        </Paper>
      )}

      <Box
        ref={triggerRef}
        className="user-allocations-tally__trigger"
        aria-label="Deal lead and support tally"
        onMouseEnter={() => setHoverOpen(true)}
        onMouseLeave={handleTriggerLeave}
      >
        <Box className="user-allocations-tally__trigger-icon-wrap">
          <GroupsOutlinedIcon className="user-allocations-tally__trigger-icon" />
          {!loading && peopleCount > 0 && (
            <Box component="span" className="user-allocations-tally__badge">
              {peopleCount}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
